import { Button, Modal } from '@clearcover/atlas';
import { PolicyDetailsType, VehicleType } from '@clearcover/core-app-types';
import classnames from 'classnames';
import React, { FC, useContext } from 'react';
import { formatAddress, formatDateTimeShortUsingTimezone } from 'Shared/utils';

import { editPolicyGaragingAddressPolicy } from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { CardHeader } from 'Shared/components';
import { Card } from 'Shared/components/Card';
import { CardDataSection } from 'Shared/components/Card/CardDataSection';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { useModalHashChange } from 'Shared/hooks';
import { MultilineAddress } from '../Address';
import { ConflictingDraftModal } from '../ConflictingDraftModal';
import { EditDetailsForm } from './EditDetailsForm';
import styles from './styles.module.css';

type PolicyDetailSectionTypes = {
  policyDetails?: PolicyDetailsType;
  vehicles?: Array<VehicleType>;
};
const GARAGING_ADDRESS_INDEX = 0;
const addressInfoHeaders = ['Garaging address', 'Mailing address'];

export const PolicyDetailSection: FC<PolicyDetailSectionTypes> = ({
  policyDetails,
  vehicles,
}) => {
  const { isEndorseablePolicy, resourceEffectiveGrants, loading } = useContext(
    PolicyContext
  );

  const loaded = !!policyDetails && !!vehicles && !loading;
  const canEditGaragingAddress =
    isEndorseablePolicy &&
    editPolicyGaragingAddressPolicy(resourceEffectiveGrants);

  const { modalHashChange } = useModalHashChange('editPolicyDetails');
  return (
    <Card id="policy-details-card">
      <CardHeader title="Policy Details">
        {loaded && canEditGaragingAddress && (
          <Modal
            onVisibilityChange={modalHashChange}
            variant="full"
            toggle={openEditDetailsModal => (
              <ConflictingDraftModal
                onApprove={openEditDetailsModal}
                renderToggle={openModal => (
                  <Button
                    className={styles.editDetailsButton}
                    size="sm"
                    variant="secondary"
                    onClick={openModal}
                    data-testid="open-edit-details-modal"
                  >
                    <span>Edit</span>
                  </Button>
                )}
              />
            )}
          >
            {close => (
              <EditDetailsForm
                policyDetails={policyDetails}
                vehicles={vehicles}
                close={close}
              />
            )}
          </Modal>
        )}
      </CardHeader>
      <CardDataSection caption="general Info" id="general-info">
        <CardSectionInfoPairs
          dataFontStyle="none"
          headers={['Policy Number', 'Bind Date', 'Effective']}
          data={
            loaded
              ? [
                  policyDetails.policyNumber,
                  formatDateTimeShortUsingTimezone(
                    policyDetails.bindDate,
                    policyDetails.policyTimeZone
                  ),
                  `${formatDateTimeShortUsingTimezone(
                    policyDetails.effectivePeriod.startsAt,
                    policyDetails.policyTimeZone
                  )}-${formatDateTimeShortUsingTimezone(
                    policyDetails.effectivePeriod.endsAt,
                    policyDetails.policyTimeZone
                  )}`,
                ]
              : Array(3).fill('')
          }
          loading={!loaded}
        />
      </CardDataSection>
      <CardDataSection
        className={classnames('fs-exclude', styles.address)}
        caption="Address"
        id="address-info"
      >
        <CardSectionInfoPairs
          dataFontStyle="none"
          hiddenColumns={
            loaded && policyDetails.showGaragingAddress
              ? [addressInfoHeaders[GARAGING_ADDRESS_INDEX]]
              : []
          }
          headers={addressInfoHeaders}
          data={
            loaded
              ? [
                  {
                    display: (
                      <MultilineAddress
                        testId="garage-address-multi"
                        address={policyDetails.addresses.garaging}
                        className={styles.value}
                      />
                    ),
                    value: formatAddress(policyDetails.addresses.garaging),
                  },
                  {
                    display: (
                      <MultilineAddress
                        testId="mailing-address-multi"
                        address={policyDetails.addresses.mailing}
                        className={styles.value}
                      />
                    ),
                    value: formatAddress(policyDetails.addresses.mailing),
                  },
                ]
              : Array(2).fill('')
          }
          loading={!loaded}
        />
      </CardDataSection>
    </Card>
  );
};
